import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, FileSignature, UserPlus, ArrowLeft } from 'lucide-react';
import { employeeApi, errMsg } from '../api';
import { WorkforceAuthPage, WorkforceHero, PasswordInput } from '../WorkforceTheme';
import { contractText, CONTRACT_TITLE } from '../contractText';
import { formatPhoneNumber } from '../../ops/components/PhoneInput';

const F = ({ label, children }) => (
  <div><label className="text-xs text-gray-400 block mb-1.5">{label}</label>{children}</div>
);
const inputCls = 'bg-black/60 border-gray-700 text-white';
const cardCls = 'mt-2 rounded-2xl border border-[#D4AF37]/25 bg-black/60 backdrop-blur-md p-5 sm:p-6 space-y-4 shadow-[0_0_60px_rgba(212,175,55,0.08)]';
const goldBtn = 'w-full bg-gradient-to-b from-[#F0CB5C] to-[#C99E2E] text-black hover:from-[#D4AF37] hover:to-[#B8941F] font-brand font-extrabold uppercase tracking-wide py-6';

const StepDots = ({ step }) => (
  <div className="flex items-center justify-center gap-2 mb-4" data-testid="join-step-dots">
    {['credentials', 'profile', 'contract'].map((s, i) => (
      <React.Fragment key={s}>
        {i > 0 && <div className="h-px w-8 bg-gray-700" />}
        <span className={`h-2.5 w-2.5 rounded-full ${s === step ? 'bg-[#D4AF37]' : 'bg-gray-700'}`} />
      </React.Fragment>
    ))}
  </div>
);

export default function EmployeeJoin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [step, setStep] = useState('credentials'); // credentials | profile | contract
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [f, setF] = useState({ email: '', password: '', phone: '', address: '', date_of_birth: '', emergency_contact_name: '', emergency_contact_phone: '', position: '' });
  const [emailConfirm, setEmailConfirm] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [agree, setAgree] = useState(false);
  const [typedName, setTypedName] = useState('');

  useEffect(() => {
    employeeApi.linkInfo(token).then((r) => setInvite(r.data)).catch(() => setInvalid(true));
  }, [token]);

  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const emailMismatch = emailConfirm && f.email.trim().toLowerCase() !== emailConfirm.trim().toLowerCase();
  const passwordMismatch = passwordConfirm && f.password !== passwordConfirm;

  const continueCredentials = () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) return setError('Enter a valid email address');
    if (f.password.length < 6) return setError('Password must be at least 6 characters');
    if (f.email.trim().toLowerCase() !== emailConfirm.trim().toLowerCase()) return setError('Emails do not match');
    if (f.password !== passwordConfirm) return setError('Passwords do not match');
    setError(''); setStep('profile');
  };

  const sign = async () => {
    setBusy(true); setError('');
    try {
      const r = await employeeApi.register({ token, ...f, contract_agree: agree, contract_typed_name: typedName });
      localStorage.setItem('employee_token', r.data.token);
      navigate('/workforce/home');
    } catch (err) {
      setError(errMsg(err, 'Could not create your account'));
    } finally { setBusy(false); }
  };

  if (invalid) return (
    <WorkforceAuthPage showStrip={false}>
      <WorkforceHero />
      <p className="text-gray-300 mt-2 max-w-sm text-center" data-testid="invite-invalid">This link is no longer valid. Ask BuildBroz to send you a new one.</p>
    </WorkforceAuthPage>
  );
  if (!invite) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="animate-spin text-[#D4AF37]" size={28} /></div>;

  return (
    <WorkforceAuthPage showStrip={step === 'credentials'}>
      <div className="w-full max-w-md">
        <WorkforceHero subtitle={step === 'credentials' ? `Welcome, ${invite.name}! Let's set up your secure login.` : null} />
        <StepDots step={step} />

        {step === 'credentials' && (
          <div className={cardCls} data-testid="join-credentials-form">
            <div className="flex items-center gap-2">
              <span className="h-8 w-8 rounded-full border border-[#D4AF37]/60 flex items-center justify-center"><UserPlus size={14} className="text-[#D4AF37]" /></span>
              <div>
                <h1 className="text-white font-brand font-extrabold text-lg leading-tight">Create Your Login</h1>
                <p className="text-[11px] text-gray-400">You'll sign in to the Workforce Portal with your email and password.</p>
              </div>
            </div>
            <F label="Email *"><Input type="email" value={f.email} onChange={set('email')} autoCapitalize="none" placeholder="you@example.com" className={inputCls} data-testid="join-email" /></F>
            <div>
              <F label="Re-enter Email *"><Input type="email" value={emailConfirm} onChange={(e) => setEmailConfirm(e.target.value)} autoCapitalize="none" className={`${inputCls} ${emailMismatch ? 'border-red-500' : ''}`} data-testid="join-email-confirm" /></F>
              {emailMismatch && <p className="text-red-400 text-xs mt-1" data-testid="join-email-mismatch">Emails do not match</p>}
            </div>
            <F label="Password * (at least 6 characters)"><PasswordInput value={f.password} onChange={set('password')} testId="join-password" /></F>
            <div>
              <F label="Re-enter Password *"><PasswordInput value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} testId="join-password-confirm" /></F>
              {passwordMismatch && <p className="text-red-400 text-xs mt-1" data-testid="join-password-mismatch">Passwords do not match</p>}
            </div>
            {error && <p className="text-red-400 text-sm" data-testid="join-credentials-error">{error}</p>}
            <Button onClick={continueCredentials} className={goldBtn} data-testid="join-credentials-continue-btn">Continue</Button>
          </div>
        )}

        {step === 'profile' && (
          <div className={cardCls} data-testid="join-profile-form">
            <div>
              <h1 className="text-white font-brand font-extrabold text-lg">Your Profile</h1>
              <p className="text-xs text-gray-400 mt-1">Only you and BuildBroz Ops can see this information.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Phone"><Input value={f.phone} onChange={(e) => setF((x) => ({ ...x, phone: formatPhoneNumber(e.target.value) }))} type="tel" className={inputCls} data-testid="join-phone" /></F>
              <F label="Date of Birth"><Input type="date" value={f.date_of_birth} onChange={set('date_of_birth')} className={inputCls} data-testid="join-dob" /></F>
            </div>
            <F label="Home Address"><Input value={f.address} onChange={set('address')} className={inputCls} data-testid="join-address" /></F>
            <F label="Position / Trade"><Input value={f.position} onChange={set('position')} placeholder={invite.position || 'e.g. Carpenter'} className={inputCls} data-testid="join-position" /></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Emergency Contact"><Input value={f.emergency_contact_name} onChange={set('emergency_contact_name')} className={inputCls} data-testid="join-emergency-name" /></F>
              <F label="Emergency Phone"><Input value={f.emergency_contact_phone} onChange={(e) => setF((x) => ({ ...x, emergency_contact_phone: formatPhoneNumber(e.target.value) }))} type="tel" className={inputCls} data-testid="join-emergency-phone" /></F>
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setError(''); setStep('credentials'); }} className="border-gray-700 bg-transparent text-gray-300 hover:bg-white/5 hover:text-white" data-testid="join-back-to-credentials-btn"><ArrowLeft size={14} /> Back</Button>
              <Button onClick={() => { setError(''); setStep('contract'); }} className={`flex-1 ${goldBtn} w-auto`} data-testid="join-continue-btn">Continue to Agreement</Button>
            </div>
          </div>
        )}

        {step === 'contract' && (
          <div className={cardCls} data-testid="join-contract-step">
            <div className="flex items-center gap-2">
              <FileSignature size={18} className="text-[#D4AF37]" />
              <h1 className="text-white font-brand font-extrabold text-base">{CONTRACT_TITLE}</h1>
            </div>
            <div className="bg-black/70 border border-gray-800 rounded-lg p-4 h-72 overflow-y-auto text-xs text-gray-300 whitespace-pre-wrap" data-testid="contract-text">
              {contractText(typedName || invite.name, new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }))}
            </div>
            <div className="space-y-3">
              <label className="flex items-start gap-2.5 text-sm text-gray-200 cursor-pointer">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-[#D4AF37]" data-testid="contract-agree-checkbox" />
                I confirm that I have reviewed this Independent Contractor Agreement and agree to sign it electronically.
              </label>
              <F label="Type your legal name to sign"><Input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Full legal name" className={inputCls} data-testid="contract-typed-name" /></F>
              {typedName && <div className="font-script text-3xl text-[#D4AF37] px-1" data-testid="contract-signature-preview">{typedName}</div>}
              {error && <p className="text-red-400 text-sm" data-testid="join-error">{error}</p>}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep('profile')} disabled={busy} className="border-gray-700 bg-transparent text-gray-300 hover:bg-white/5 hover:text-white">Back</Button>
                <Button onClick={sign} disabled={busy || !agree || !typedName.trim()} className={`flex-1 ${goldBtn} w-auto`} data-testid="sign-agreement-btn">
                  {busy && <Loader2 size={16} className="animate-spin" />} Sign Agreement
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </WorkforceAuthPage>
  );
}
